/**
 * =========================================================================
 * LOADING COMPONENTS
 * =========================================================================
 * 
 * This file provides various loading indicators for different use cases.
 * All components use consistent styling with the app's color scheme.
 * 
 * Component Variants:
 * - Loading: Default full-screen spinner
 * - LoadingSpinners: Animated dots with bounce effect
 * - LoadingInline: Small inline spinner for buttons/forms
 * - LoadingSkeleton: Skeleton loading for content placeholders
 * - LoadingAuth: Specialized loading for authentication flows
 * 
 * Design System:
 * - Primary color: #F15213 (brand orange)
 * - Consistent animations and timing
 * - Accessible loading states
 * - Responsive design considerations
 * =========================================================================
 */

// src/layouts/loading.js

import React from "react";

/**
 * Default Loading Component
 * 
 * Full-screen loading spinner for major app transitions.
 * Used during route changes, initial app load, and major operations.
 * 
 * Features:
 * - Centered positioning
 * - Smooth spin animation
 * - Brand color accent
 * - Full viewport coverage
 */
export default function Loading() {
  return (
    <div className="flex items-center justify-center h-screen bg-gray-50/80">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[#F15213]"></div>
    </div>
  );
}

/**
 * LoadingSpinners Component
 * 
 * Animated dots with bounce effect for engaging loading states.
 * Best used for content loading or form submissions.
 * 
 * Features:
 * - Three bouncing dots
 * - Staggered animation timing
 * - Loading text indicator
 * - Brand color consistency
 */
export function LoadingSpinners() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="flex space-x-2">
        <div className="w-3 h-3 bg-[#F15213] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-3 h-3 bg-[#F15213] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-3 h-3 bg-[#F15213] rounded-full animate-bounce"></div>
      </div>
      <div className="mt-4 text-gray-600 font-medium">Loading...</div>
    </div>
  );
}

/**
 * LoadingInline Component
 * 
 * Compact inline spinner for buttons, forms, and small components.
 * Perfect for loading states that don't require full-screen coverage.
 * 
 * Features:
 * - Inline flex layout
 * - Small footprint
 * - Customizable text
 * - Maintains text flow
 */
export function LoadingInline({ text = "Loading..." }) {
  return (
    <div className="inline-flex items-center">
      <div className="w-4 h-4 border-2 border-[#F15213] border-t-transparent rounded-full animate-spin"></div>
      <span className="ml-2 text-gray-600">{text}</span>
    </div>
  );
}

/**
 * LoadingSkeleton Component
 * 
 * Skeleton placeholder for content areas while data loads.
 * Provides visual feedback about upcoming content structure.
 * 
 * Features:
 * - Pulse animation effect
 * - Varied width bars for realistic preview
 * - Responsive layout
 * - Subtle gray color scheme
 */
export function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            <div className="h-3 bg-gray-200 rounded w-1/3"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * LoadingAuth Component
 * 
 * Specialized loading for authentication flows and user state checks.
 * Provides context-specific messaging for auth operations.
 * 
 * Features:
 * - Authentication-focused messaging
 * - Brand color styling
 * - Professional appearance
 * - Clear user communication
 */
export function LoadingAuth({ message = "Authenticating..." }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-[#F15213] mb-4"></div>
      <div className="text-gray-700 font-medium text-lg">{message}</div>
      <div className="text-gray-500 text-sm mt-2">Please wait while we verify your credentials</div>
    </div>
  );
}

/**
 * LoadingCard Component
 * 
 * Loading state for card-based layouts and components.
 * Maintains card structure while showing loading state.
 * 
 * Features:
 * - Card-shaped container
 * - Centered spinner
 * - Maintains layout structure
 * - Subtle background
 */
export function LoadingCard({ height = "h-32" }) {
  return (
    <div className={`${height} bg-white rounded-lg border border-gray-200 flex items-center justify-center`}>
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-[#F15213]"></div>
    </div>
  );
}

/**
 * LoadingButton Component
 * 
 * Loading state for buttons during form submissions or actions.
 * Replaces button content while maintaining button structure.
 * 
 * Features:
 * - Button-sized spinner
 * - Maintains button dimensions
 * - Disabled appearance
 * - Consistent styling
 */
export function LoadingButton() {
  return (
    <div className="inline-flex items-center justify-center">
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
      <span className="ml-2">Processing...</span>
    </div>
  );
}
